import type { StateCreator } from 'zustand'
import { dataCatalogState , BoundingBox} from './dataCatalogSlice'
import { loadingState } from './loadingSlice'

export const MODAL_TYPE = {
  uploadFile: 'uploadFile',
  callConfig: 'callConfig',
  regionSelection: 'regionSelection',
}

// TODO: Test this

export interface ModalSliceState {
  currentOpenedModal: string | null
  modalData: Record<string, any> | null // eslint-disable-line
  uploadCallbacks?: {
    onStart?: (fileName: string, documentType: string, options?: {
      fileSize?: number
      multiPageProps?: {
        multi_page_set_id: string
        sheet_index: string
        sheet_count: string
        multi_page_set_name: string
      }
      // FS-499: when the caller has pre-registered this file via
      // POST /v2/files/register-pending, the server-issued docId is
      // passed in here so the local temp row uses the same id. That
      // makes the temp row a thin alias for a real DB row that
      // survives page refresh.
      docId?: string
    }) => string | void
    onComplete?: (fileName: string) => void
    // FS-499: called when a pre-registered file is dropped before its
    // upload starts. Removes the temp row entirely instead of
    // transitioning it to 'processing'.
    onDiscard?: (docId: string) => void
    getAbortSignal?: (fileName: string) => AbortSignal | undefined
  }
  openModal: (modalName: string) => void
  closeModal: () => void
  // TODO: Need to add data type that's generic
  setModalData: (modalName: string, data: any) => void // eslint-disable-line
  setUploadCallbacks: (callbacks?: {
    onStart?: (fileName: string, documentType: string, options?: {
      fileSize?: number
      multiPageProps?: {
        multi_page_set_id: string
        sheet_index: string
        sheet_count: string
        multi_page_set_name: string
      }
      docId?: string
    }) => string | void
    onComplete?: (fileName: string) => void
    onDiscard?: (docId: string) => void
    getAbortSignal?: (fileName: string) => AbortSignal | undefined
  }) => void
  onModalSubmit: (modalName: string, data: any) => void // eslint-disable-line
}

export const initialModalState = {
  currentOpenedModal: null,
  modalData: null,
  uploadCallbacks: undefined,
}

export const modalSlice: StateCreator<
  ModalSliceState & dataCatalogState & loadingState,
  [],
  [],
  ModalSliceState
> = (set, get) => ({
  ...initialModalState,
  openModal: (modalName) => {
    set({ currentOpenedModal: modalName })
  },
  closeModal: () => {
    set({ currentOpenedModal: null })
  },
  setModalData: (modalName, data) => {
    set({ modalData: { [modalName]: data } })
  },
  setUploadCallbacks: (callbacks) => {
    set({ uploadCallbacks: callbacks })
  },
  onModalSubmit: async (modalName, data) => {
    const { uploadDocument, closeModal, uploadCallbacks } = get()

    if (!data) return
    closeModal()

    switch (modalName) {
      case MODAL_TYPE.uploadFile:
      case MODAL_TYPE.regionSelection: {
        // Strip id and name from bboxes before sending to backend
        const cleanedBboxes = data.bboxes?.map((bbox: BoundingBox) => {
          const { x, y, width, height, type, page } = bbox
          return { x, y, width, height, type, ...(page != null && { page }) }
        })
        const fileName = data.file?.name

        // FS-499: if the caller pre-registered this file via
        // /register-pending, `data.docId` holds the server-issued docId and
        // onStart was already invoked at batch entry — skip the second call.
        const preRegisteredDocId: string | undefined =
          typeof data.docId === 'string' ? data.docId : undefined

        // Call onStart callback if provided (returns upload_started_at for sort ordering)
        let uploadStartedAt: string | undefined
        if (!preRegisteredDocId && uploadCallbacks?.onStart && fileName) {
          const result = uploadCallbacks.onStart(fileName, data.type, { fileSize: data.file?.size })
          if (typeof result === 'string') uploadStartedAt = result
        }

        // Get abort signal for this upload if available
        const signal = uploadCallbacks?.getAbortSignal?.(fileName)

        try {
          await uploadDocument(data.file, {
            name: data.name,
            description: data.description,
            type: data.type,
            ...(cleanedBboxes && { bboxes: cleanedBboxes }),
            ...(data.totalPdfPages && { properties: {
              total_pdf_pages: String(data.totalPdfPages),
              ...(data.pdfPageNumbers && { pdf_page_numbers: JSON.stringify(data.pdfPageNumbers) }),
            } }),
          }, {
            signal,
            skipToast: true, // Suppress toast - DataCatalog shows its own success/cancel toast
            autoRename: data.autoRename,
            replaceDocumentId: data.replaceDocumentId,
            uploadStartedAt,
            docId: preRegisteredDocId,
          })
        } finally {
          // Call onComplete callback if provided
          if (uploadCallbacks?.onComplete && fileName) {
            uploadCallbacks.onComplete(fileName)
          }
        }
      break
      }
      default:
        return null
    }
  },
})
